import React from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Link,
  Grid,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PolicyIcon from "@mui/icons-material/Policy";
import ArticleIcon from "@mui/icons-material/Article";
import HomeIcon from "@mui/icons-material/Home";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import codingComitfavlogo from "../assets/codingcoachlogo-favicon.png";

const Footer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 🎨 Brand-inspired background gradient
  const bgGradient = isDark
    ? "linear-gradient(135deg, #0b0012 0%, #1a001a 50%, #2d004d 100%)"
    : "linear-gradient(135deg, #fff0f3 0%, #f7e8ff 100%)";

  const accent = "#e50914"; // red from logo
  const accentSecondary = "#6a0dad"; // purple from logo
  const textPrimary = isDark ? "#F9FAFB" : "#1A1A1A";
  const textSecondary = isDark ? "#C9CED6" : "#333";
  const linkHover = isDark ? "#ff80d5" : "#8e24aa";

  return (
    <Box
      component="footer"
      sx={{
        background: bgGradient,
        color: textPrimary,
        py: { xs: 3, sm: 5 },
        borderTop: isDark
          ? `2px solid rgba(255,255,255,0.08)`
          : `2px solid rgba(0,0,0,0.05)`,
        fontFamily: "Poppins, Roboto, Arial, sans-serif",
        transition: "background 0.4s ease, color 0.3s ease",
        boxShadow: isDark
          ? "0 -4px 16px rgba(229,9,20,0.2)"
          : "0 -2px 10px rgba(106,13,173,0.15)",
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={4}
          justifyContent="space-between"
          alignItems="flex-start"
          textAlign={{ xs: "center", md: "left" }}
        >
          {/* 🚀 BRANDING */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box
              component={Link}
              href="/"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
                gap: 1.5,
                textDecoration: "none",
                mb: 1.5,
              }}
            >
              <img
                src={codingComitfavlogo}
                alt="CodingComet Logo"
                style={{ width: 60, height: 60 }}
              />
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  background: `linear-gradient(90deg, ${accent}, ${accentSecondary})`,
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: 22,
                  letterSpacing: 0.5,
                }}
              >
                CodingComet
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: textSecondary,
                fontSize: { xs: 13.5, sm: 14 },
                lineHeight: 1.7,
                maxWidth: 320,
                mx: { xs: "auto", md: 0 },
              }}
            >
              Learn, Code & Grow — Empowering developers through clarity and
              creativity.
            </Typography>
          </Grid>

          {/* ⚡ QUICK LINKS */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 16, sm: 18 },
                mb: 2,
                background: `linear-gradient(90deg, ${accent}, ${accentSecondary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Quick Links
            </Typography>

            <Stack
              spacing={1.2}
              sx={{
                "& a": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isMobile ? "center" : "flex-start",
                  gap: 0.6,
                  color: textPrimary,
                  fontSize: { xs: 15, sm: 16 },
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: linkHover,
                    transform: "translateX(5px)",
                  },
                },
              }}
            >
              <Link href="/"><HomeIcon fontSize="small" />Home</Link>
              <Link href="/about"><ArticleIcon fontSize="small" />About</Link>
              <Link href="/privacy"><PolicyIcon fontSize="small" />Privacy Policy</Link>
              <Link href="/help"><HelpCenterIcon fontSize="small" />Help Center</Link>
            </Stack>
          </Grid>

          {/* 💬 CONTACT SECTION */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 16, sm: 18 },
                mb: 2,
                background: `linear-gradient(90deg, ${accent}, ${accentSecondary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Contact
            </Typography>

            <Stack spacing={1.4}>
              <Stack direction="row" alignItems="center" spacing={1.2} justifyContent={isMobile ? "center" : "flex-start"}>
                <EmailIcon fontSize="small" sx={{ color: accent }} />
                <Link
                  href="mailto:rkchy23@gmail.com"
                  sx={{
                    color: textPrimary,
                    textDecoration: "none",
                    fontSize: { xs: 14, sm: 15 },
                    "&:hover": { color: linkHover },
                  }}
                >
                  rkchy23@gmail.com
                </Link>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1.2} justifyContent={isMobile ? "center" : "flex-start"}>
                <PhoneIcon fontSize="small" sx={{ color: accent }} />
                <Link
                  href="tel:+918528793595"
                  sx={{
                    color: textPrimary,
                    textDecoration: "none",
                    fontSize: { xs: 14, sm: 15 },
                    "&:hover": { color: linkHover },
                  }}
                >
                  +91 8528793595
                </Link>
              </Stack>

              <Stack direction="row" spacing={1.5} justifyContent={isMobile ? "center" : "flex-start"} mt={1.5}>
                <IconButton
                  href="https://www.linkedin.com/in/rajesh-kumar-chaudhary-a71050238/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: textPrimary,
                    border: `1.5px solid rgba(255,255,255,0.25)`,
                    backdropFilter: "blur(4px)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(229,9,20,0.1)",
                      color: "#0A66C2",
                      borderColor: "#0A66C2",
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider
          sx={{
            my: { xs: 3, sm: 4 },
            borderColor: isDark
              ? "rgba(255,255,255,0.2)"
              : "rgba(0,0,0,0.1)",
            boxShadow: isDark
              ? "0 0 6px rgba(229,9,20,0.3)"
              : "0 0 6px rgba(106,13,173,0.2)",
          }}
        />

        {/* COPYRIGHT */}
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: textSecondary,
            fontSize: { xs: 13, sm: 14 },
          }}
        >
          © {new Date().getFullYear()}{" "}
          <strong
            style={{
              background: `linear-gradient(90deg, ${accent}, ${accentSecondary})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            CodingComet
          </strong>
          . All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
